import React, { useEffect, useState } from 'react';
import { ExternalLink, FileDown, Loader2, Pencil } from 'lucide-react';
import { errorText } from '../ErrorBanner';
import { useAlert } from '../../contexts/AlertContext';
import { intakeApi } from '../../services/intakeApi';
import { IntakeForm } from '../../types/intake';
import { IntakeFields } from './IntakeFields';

const NA = <span className="text-amber-600">확인 필요</span>;

/**
 * 검토 탭의 사업 접수서(2026-10-22) — 요약을 보이고, 「고치기」로 칸 1~8 을 연다.
 * 「Word로 저장」은 드라이브 01 폴더에 새 파일을 올린다(앱은 드라이브 파일을 덮지 않는다).
 */
export const IntakeCard: React.FC<{ projectId: number; intake: IntakeForm; hasBid: boolean; onSaved?: (f: IntakeForm) => void }> = ({ projectId, intake, hasBid, onSaved }) => {
  const { showToast } = useAlert();
  const [saved, setSaved] = useState(intake);
  const [draft, setDraft] = useState<IntakeForm | null>(null);
  /* 요약이 생겨 빈 칸이 채워지면 위에서 새 값이 온다 — 고치는 중이 아닐 때만 받는다 */
  useEffect(() => { if (!draft) setSaved(intake); }, [intake]); // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState<'save' | 'docx' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [docUrl, setDocUrl] = useState<string | null>(intake.docUrl);
  const f = saved;

  const save = async () => {
    if (!draft) return;
    setBusy('save'); setError(null);
    try { const f = await intakeApi.save(projectId, draft); setSaved(f); setDraft(null); onSaved?.(f); showToast('접수서를 저장했습니다', 'success'); }
    catch (e) { setError(errorText(e, '저장하지 못했습니다')); }
    finally { setBusy(null); }
  };
  const docx = async () => {
    setBusy('docx'); setError(null);
    try { const r = await intakeApi.docx(projectId); setDocUrl(r.url); showToast('드라이브 「01.공고문·제안요청서」에 접수서 Word 를 올렸습니다', 'success'); }
    catch (e) { setError(errorText(e, 'Word 로 저장하지 못했습니다')); }
    finally { setBusy(null); }
  };

  const row = (label: string, v: React.ReactNode) => (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-900 min-w-0 break-words whitespace-pre-line">{v}</dd>
    </>
  );
  const val = (s: string | null) => (s?.trim() ? s : NA);

  return (
    <section className="bg-white rounded-lg border border-slate-200">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">사업 접수서</h2>
        <span className="text-xs text-slate-400">{f.receivedAt} · {f.source ?? '출처 미상'} · {f.receivedBy}</span>
        <div className="ml-auto flex items-center gap-1.5">
          {draft ? (
            <>
              <button onClick={() => { setDraft(null); setError(null); }} disabled={busy !== null} className="px-2.5 py-1 rounded-lg text-xs text-slate-600 hover:bg-slate-100">취소</button>
              <button onClick={save} disabled={busy !== null || !draft.bizName?.trim() || !draft.clientOrg?.trim()}
                className="px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1">
                {busy === 'save' && <Loader2 size={12} className="animate-spin" />} 저장
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setDraft(saved)} className="px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1">
                <Pencil size={12} /> 고치기
              </button>
              <button onClick={docx} disabled={busy !== null} title="고친 내용으로 Word 를 새로 만들어 드라이브에 올립니다"
                className="px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1">
                {busy === 'docx' ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />} Word로 저장
              </button>
              {docUrl && <a href={docUrl} target="_blank" rel="noreferrer" title="드라이브의 최근 접수서 Word" className="text-xs text-indigo-600 hover:underline flex items-center gap-0.5">Word 열기 <ExternalLink size={11} /></a>}
            </>
          )}
        </div>
      </div>
      {error && <p role="alert" className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
      <div className="px-4 py-3">
        {draft ? (
          <IntakeFields value={draft} onChange={setDraft} showEligibility={hasBid || !!draft.noticeNo || !!draft.qualification} />
        ) : (
          <dl className="grid grid-cols-[96px_1fr] gap-y-1.5 gap-x-4 text-sm">
            {row('발주처', val([f.clientOrg, f.clientDept].filter(Boolean).join(' · ') || null))}
            {row('담당자', val([f.contactName, f.contactPhone, f.contactEmail].filter(Boolean).join(' · ') || null))}
            {row('유형 · 기간', val([f.bizType, f.period].filter(Boolean).join(' · ') || null))}
            {row('추정가격', val(f.budget))}
            {row('제출 마감', val(f.dueAt))}
            {row('과업 범위', val(f.scope))}
            {(f.qualification || f.trackRecord) && row('자격 · 실적', [f.qualification, f.trackRecord].filter(Boolean).join('\n'))}
            {f.documents.length > 0 && row('제출 서류', f.documents.join(' · '))}
            {f.files.length > 0 && row('원본', (
              <span className="flex flex-wrap gap-x-3">
                {f.files.map((x) => x.url
                  ? <a key={x.name} href={x.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">{x.name}</a>
                  : <span key={x.name}>{x.name}</span>)}
              </span>
            ))}
            {f.unknowns.length > 0 && row('확인 필요', <span className="text-amber-700">{f.unknowns.join(' · ')}</span>)}
            {f.next && row('다음 할 일', f.next)}
          </dl>
        )}
      </div>
    </section>
  );
};
